import { useZero } from "@rocicorp/zero/react";
import { useCallback, useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { namesMatch } from "../../shared/normalizeName";
import { queries } from "../../shared/queries";
import { config } from "../environment";
import { useChordShortcuts } from "../hooks/useChordShortcuts";
import { useColorMode } from "../hooks/useColorMode";
import { useCommandBar } from "../hooks/useCommandBar";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { useIntercomMessenger } from "../hooks/useIntercomMessenger";
import { useKonamiCode } from "../hooks/useKonamiCode";
import { useQuery } from "../hooks/useQuery";
import { browserNotificationSettings } from "../lib/browserNotifications";
import {
  clearPostSignupFormViewRecord,
  hasRecordedPostSignupFormView,
  markPostSignupFormViewRecorded,
} from "../lib/postSignupFormView";
import {
  clearSignupAttribution,
  readSignupAttribution,
} from "../lib/signupAttribution";
import { soundSettings } from "../lib/sounds";
import { navigate, Route, routes, Switch } from "../lib/urls";
import { setSealingKey } from "../sealSecret";
import { Connectors } from "./Connectors";
import { CommandBar } from "./CommandBar";
import { CompleteProfile } from "./CompleteProfile";
import { Credits } from "./Credits";
import { Favorites } from "./Favorites";
import { GalleryImportRoute } from "./GalleryImportRoute";
import { GlobalModals } from "./GlobalModals";
import { IntercomMessenger } from "./IntercomMessenger";
import { Links } from "./Links";
import { markCameFromSignupFlow, NewUserIntroVideo } from "./NewUserIntroVideo";
import { MaintenanceToast } from "./MaintenanceToast";
import { PersonalChats } from "./PersonalChats";
import { Recents } from "./Recents";
import { Settings } from "./Settings";
import { Monitoring } from "./Monitoring";
import { Skills } from "./Skills";
import { Space } from "./Space";
import { ToastProvider } from "./Toast";
import { Watchtower } from "./Watchtower";
import { Workflow } from "./Workflow";

export function SignedIn() {
  const z = useZero();
  const [user] = useQuery(queries.currentUser());
  const [tenant] = useQuery(queries.currentTenant());
  const [preferences] = useQuery(queries.userPreferences());
  const [tenantKeys, tenantKeysResult] = useQuery(queries.tenantSealingKeys());
  const sealingKey =
    tenantKeys.find((key) => key.domain === "credentials")?.publicKey ??
    (tenantKeysResult.type === "complete" ? null : undefined);
  setSealingKey(sealingKey);

  useColorMode({
    bindListeners: true,
    preference:
      (preferences?.theme as "light" | "dark" | "system") ?? undefined,
    onToggle: (theme) => z.mutate(sharedMutators.setUserPreference({ theme })),
  });
  useSoundSettings(preferences);
  useBrowserNotificationSettings(preferences);
  const { open, close } = useCommandBar();
  useChordShortcuts();

  const [creditsOpen, setCreditsOpen] = useState(false);
  useKonamiCode(useCallback(() => setCreditsOpen(true), []));

  if (!user || sealingKey === undefined) return null;

  if (config.signupProfileFormEnabled && !user.hasCompletedPostSignupForm) {
    if (!tenant) return null;
    // SSO gives us one combined name (or the email as a fallback when the IdP
    // asserts none); split it into first/last to prefill, dropping the
    // email-fallback case so we don't seed the fields with an address.
    const ssoName = namesMatch(user.name, user.email) ? "" : user.name;
    const [ssoFirstName = "", ...ssoRestName] = ssoName.split(" ");
    return (
      <>
        <RecordPostSignupFormView />
        <CompleteProfile
          initialFirstName={ssoFirstName}
          initialLastName={ssoRestName.join(" ")}
          initialCompanyName={tenant.companyName ?? ""}
          companyNameLocked={Boolean(tenant.companyName)}
          onSubmit={(profile) => {
            // Must run before the optimistic completeProfile update can mount NewUserIntroVideo on Recents, where the key is read only on mount.
            markCameFromSignupFlow();
            z.mutate(
              sharedMutators.completeProfile({
                ...profile,
                marketingAttribution: readSignupAttribution(),
              })
            );
            clearSignupAttribution();
            clearPostSignupFormViewRecord();
          }}
        />
      </>
    );
  }

  return (
    <ToastProvider>
      {open && <CommandBar open={open} onClose={close} />}
      {creditsOpen && <Credits onClose={() => setCreditsOpen(false)} />}
      <GlobalModals />
      {Boolean(config.intercomAppId) && <Intercom user={user} />}
      <Switch>
        <Route path={routes.galleryImport}>
          {({ workflowId, commitId, sha256 }) => (
            <GalleryImportRoute
              workflowId={workflowId}
              commitId={commitId}
              sha256={sha256}
            />
          )}
        </Route>
        <Route path={routes.personal.workflow}>
          {({ workflowId, step }) => (
            <PersonalWorkflow workflowId={workflowId} step={step} />
          )}
        </Route>
        <Route path={routes.personal}>
          <PersonalSpaceRoute />
        </Route>
        <Route path={routes.space.workflow}>
          {({ spaceId, workflowId, step }) => (
            <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />
          )}
        </Route>
        <Route path={routes.space}>
          {({ spaceId }) => <Space spaceId={spaceId} />}
        </Route>
        <Route path={routes.recents}>
          <>
            <NewUserIntroVideo />
            <MaintenanceToast />
            <Recents />
          </>
        </Route>
        <Route path={routes.favorites}>
          <Favorites />
        </Route>
        <Route path={routes.home}>
          <RedirectToHome defaultHome={preferences?.defaultHome as string} />
        </Route>
        <Route path={routes.chats}>
          <PersonalChats />
        </Route>
        <Route path={routes.connectors}>
          <Connectors />
        </Route>
        <Route path={routes.skills}>
          <Skills />
        </Route>
        <Route path={routes.monitoring}>
          <Monitoring />
        </Route>
        <Route path={routes.links}>
          <Links />
        </Route>
        <Route path={routes.settings}>
          <Settings />
        </Route>
        <Route path={routes.watchtower}>
          <Watchtower />
        </Route>
        <Route path={routes.credits}>
          <Credits onClose={() => navigate(routes.recents)} />
        </Route>
        <Route>
          <RedirectToHome defaultHome={preferences?.defaultHome as string} />
        </Route>
      </Switch>
    </ToastProvider>
  );
}

function RecordPostSignupFormView() {
  const z = useZero();
  const userId = useCurrentUserId();
  useEffect(() => {
    if (hasRecordedPostSignupFormView(userId)) return;
    markPostSignupFormViewRecorded(userId);
    z.mutate(
      sharedMutators.recordPostSignupFormView({
        marketingAttribution: readSignupAttribution(),
      })
    );
  }, [z, userId]);
  return null;
}

function Intercom({
  user,
}: {
  user: { email: string; name: string; tenantId: string };
}) {
  const { open, close, setUnreadCount, setFailed } = useIntercomMessenger();
  return (
    <IntercomMessenger
      user={user}
      open={open}
      onClose={close}
      onUnreadChange={setUnreadCount}
      onFailedChange={setFailed}
    />
  );
}

function useSoundSettings(
  preferences:
    | {
        soundClicks?: string | null;
        soundChat?: string | null;
        soundWorkflow?: string | null;
      }
    | undefined
) {
  useEffect(() => {
    soundSettings.clicks = preferences?.soundClicks !== "off";
    soundSettings.chat = preferences?.soundChat !== "off";
    soundSettings.workflow = preferences?.soundWorkflow !== "off";
  }, [
    preferences?.soundClicks,
    preferences?.soundChat,
    preferences?.soundWorkflow,
  ]);
}

function useBrowserNotificationSettings(
  preferences: { browserNotifications?: string | null } | undefined
) {
  useEffect(() => {
    browserNotificationSettings.chat =
      preferences?.browserNotifications === "on";
  }, [preferences?.browserNotifications]);
}

function PersonalSpaceRoute() {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Space spaceId={spaceId} />;
}

function PersonalWorkflow({
  workflowId,
  step,
}: {
  workflowId: string;
  step?: string;
}) {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />;
}

function RedirectToHome({ defaultHome }: { defaultHome?: string }) {
  useEffect(() => {
    if (defaultHome === "favorites") {
      navigate(routes.favorites, undefined, { replace: true });
    } else if (defaultHome === "chats") {
      navigate(routes.chats, undefined, { replace: true });
    } else if (
      defaultHome &&
      !["recents", "spaces", "chats", "monitor", "branches"].includes(
        defaultHome
      )
    ) {
      navigate(routes.space, { spaceId: defaultHome }, { replace: true });
    } else {
      navigate(routes.recents, undefined, { replace: true });
    }
  }, [defaultHome]);
  return null;
}
